"use client";

import { useMediaQuery } from "@/hooks/mediaQuery";
import { AppContext } from "@/services/AppContext/appContext";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command";
import { Input } from "@/shadcn/components/ui/input";
import { ScrollArea } from "@/shadcn/components/ui/scroll-area";
import { Search } from "lucide-react";
import type React from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

type SideBarSearchProps = {
  hideSidebar: () => void;
};

const SideBarSearch: React.FC<SideBarSearchProps> = ({ hideSidebar }) => {
  const { t } = useTranslation();
  const { conversations } = useContext(AppContext);
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const isNotSmall = useMediaQuery("sm");

  // Handle clicks outside to close the popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        open &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        popoverContentRef.current &&
        !popoverContentRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Handle input focus
  const handleInputFocus = () => {
    setOpen(true);
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setOpen(true); // Ensure popover stays open while typing
  };

  // Handle chat selection
  const handleSelectChat = (chatId: number) => {
    nav(`/chat/${chatId}`);
    setOpen(false);
    setSearchQuery("");
    if (!isNotSmall) {
      hideSidebar();
    }
  };

  return (
    <div className="relative mb-2 block">
      <div className="relative w-full">
        <Search className="text-muted-foreground absolute top-1/2 left-2 h-4 w-4 -translate-y-1/2" />
        <Input
          ref={inputRef}
          placeholder="Search chats..."
          className="w-full pl-8"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={() => setOpen(true)}
        />
      </div>

      {open && (
        <div
          ref={popoverContentRef}
          className="bg-popover text-popover-foreground animate-in absolute z-50 mt-1 w-full rounded-md border p-0 shadow-md outline-none"
        >
          <Command>
            <CommandList>
              <ScrollArea className="h-[200px]">
                <CommandEmpty>{t("No chats")}</CommandEmpty>
                <CommandGroup>
                  {conversations &&
                    [...conversations]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .filter(
                        (chat) =>
                          searchQuery === "" ||
                          chat.name
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase())
                      )
                      .map((chat) => (
                        <CommandItem
                          key={chat.id}
                          onSelect={() => handleSelectChat(chat.id)}
                          className="cursor-pointer"
                          value={chat.name + "_" + chat.id}
                        >
                          {chat.name}
                        </CommandItem>
                      ))}
                </CommandGroup>
              </ScrollArea>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
};

export default SideBarSearch;
